import {ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit} from '@angular/core';
import {
  DeclarationStatus,
  DeclarationType,
  HealthDeclarationStatus,
  PortOfCall,
  TaskMessageStatus,
  Visit
} from '@portbase/bezoekschip-service-typescriptmodels';
import {VisitContext} from '../visit-context';
import {AppContext} from '../../app-context';
import lodash from "lodash";
import {HarbourDuesService} from "../harbour-dues/services/harbour-dues.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'app-visit-status',
    templateUrl: './visit-status.component.html',
    styleUrls: ['./visit-status.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VisitStatusComponent implements OnInit {
  PAX = DeclarationType.PAX;
  STO = DeclarationType.STO;

  context = VisitContext;
  appContext = AppContext;
  harbourDuesStatus: DeclarationStatus;

  @Input()
  mobile: boolean;

  constructor(private harbourDuesService: HarbourDuesService, private destroyRef: DestroyRef) {}

  ngOnInit(): void {
    this.harbourDuesService.aggregateStatusUpdate().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.harbourDuesStatus = value.status;
    });
  }

  get useNewCargoScreen(): boolean {
    return VisitContext.shouldNavigateToNewCargo();
  }

  get visit(): Visit {
    return this.context.savedVisit;
  }

  getStatus(type: DeclarationType): DeclarationStatus {
    return this.context.declarationStatusOf(type);
  }

  getSecurityStatus(): DeclarationStatus {
    if (this.context.isVlissingenOrTerneuzenVisit()) {
      return this.context.savedVisit.securityDeclaration?.securityReportRequired ? DeclarationStatus.ACCEPTED : null;
    }
    return this.context.declarationStatusOf(DeclarationType.SECURITY)
  }

  getHamisHealthStatus(): HealthDeclarationStatus {
    if (this.context.visit.healthDeclarationSummary) {
      return this.context.visit.healthDeclarationSummary.healthDeclarationStatus
    }
    return null;
  }

  getVisitStatus(): DeclarationStatus {
    if (!this.context.visit.portOfCall.paDeclarationRequired) {
      return this.context.findLatestDeclaration(DeclarationType.WPCS) ? DeclarationStatus.ACCEPTED : null;
    }
    const visitDeclarationStatus = this.context.declarationStatusOf(DeclarationType.VISIT);
    const declarationTypes = [DeclarationType.VISIT, DeclarationType.NOD, DeclarationType.NOA];
    const isAnyDeclarationRejected = lodash.sortBy((this.context.latestDeclarations || [])
      .filter(d => declarationTypes.includes(d.type)), d => d.timeStamp)
      .some(d => d.status === DeclarationStatus.REJECTED);
    return isAnyDeclarationRejected && visitDeclarationStatus !== DeclarationStatus.DECLARED
      ? DeclarationStatus.REJECTED : visitDeclarationStatus;
  }

  getLeastTerminalMessageStatus(): TaskMessageStatus {
    return this.context.visit.visitDeclaration.portVisit.berthVisits
      .map(bv => VisitContext.getTerminalDeclarationStatus(bv))
      .reduce((a, b) =>
        VisitContext.taskMessageStatusOrdered.indexOf(a) > VisitContext.taskMessageStatusOrdered.indexOf(b) ? a : b, null)
  }

  getAuthoriseStatus() {
    if (this.visit) {
      return this.visit.cargoDeclarants && this.visit.cargoDeclarants.length > 0 ? DeclarationStatus.ACCEPTED : null;
    }
  }

  getDangerousGoodsLink(): string {
    if (VisitContext.isOrganisationOnlyCargoDeclarant()) {
      return '/details/' + this.visit.crn + '/dangerousGoods/' + AppContext.userProfile.organisation?.shortName;
    }
    return '/details/' + this.visit.crn + '/dangerousGoods';
  }

  displayNotificationOfDeparture() {
    if (this.visit) {
      const berthVisits = this.visit.visitDeclaration.portVisit.berthVisits;
      return berthVisits.length >= 1 && !!berthVisits[berthVisits.length - 1].atd;
    }
  }

  portOfCallAllowsSecurity(portOfCall: PortOfCall) {
    return portOfCall.paDeclarationRequired || this.context.isVlissingenOrTerneuzenVisit();
  }

  authorizationsEnabled() {
    return !this.context.visit.portOfCall.authoriseOrganisationsDisabled && !this.context.isAssignedFinancialDeclarant();
  }

  harbourDuesEnabled() {
    return !this.context.isUserOnlyCargoDeclarant() && this.visit.portOfCall.harbourDuesEnabled &&
      !this.context.visitInTerminalPlanning() && this.visit.visitDeclaration.portVisit.berthVisits.length > 0;
  }

  visitInfoEnabled() {
    return !this.context.isAssignedFinancialDeclarant();
  }

  securityEnabled() {
    return !this.context.isUserOnlyCargoDeclarant() && this.portOfCallAllowsSecurity(this.visit.portOfCall) &&
      !this.appContext.isCustoms() && !this.context.visitInTerminalPlanning() && !this.context.isAssignedFinancialDeclarant();
  }

  crewEnabled() {
    return !this.context.isUserOnlyCargoDeclarant() && this.appContext.hasRole('PaxDeclarant') &&
      !this.context.visitInTerminalPlanning() && !this.context.isAssignedFinancialDeclarant();
  }

  healthEnabled() {
    return !this.context.isUserOnlyCargoDeclarant()
      && (this.visit.portOfCall.port.locationUnCode === 'NLRTM' || this.visit.portOfCall.port.locationUnCode === 'NLAMS')
      && !this.context.visitInTerminalPlanning()
      && !this.context.isAssignedFinancialDeclarant();
  }

  wasteEnabled() {
    return !this.context.isUserOnlyCargoDeclarant()
      && this.visit.portOfCall.wasteEnabled
      && !this.appContext.isCustoms()
      && !this.context.visitInTerminalPlanning()
      && !this.context.isAssignedFinancialDeclarant();
  }

  shipStoresEnabled() {
    return !this.context.isUserOnlyCargoDeclarant()
      && (this.appContext.hasRole('ShipSuppliesDeclarant') || this.appContext.isCustoms())
      && !this.context.visitInTerminalPlanning()
      && !this.context.isAssignedFinancialDeclarant();
  }

  cargoImportEnabled() {
    return (this.appContext.isAdminOrCustoms() || (this.visit.cargoImportEnabled && this.context.isUserCargoImportViewer()))
      && !this.context.visitInTerminalPlanning()
      && !this.context.isAssignedFinancialDeclarant();
  }

  dangerousGoodsEnabled() {
    return this.visit.dangerousGoodsEnabled
      && !this.appContext.isCustoms()
      && !this.context.visitInTerminalPlanning()
      && !this.context.isAssignedFinancialDeclarant();
  }
}
